<template>
  <v-card>
    <v-card-title class="justify-center">
      <span class="headline">Export Laporan Excel</span>
      <v-btn absolute right fab @click="onCloseButtonClick()" small text color="primary">
        <v-icon dark>mdi-close-circle</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-2">
      <v-container>
        <v-alert type="info" text dense border="left" class="mb-2">
          <div class="text-body-2">
            <strong>Cara menggunakan filter:</strong>
            <ul class="mb-0 mt-1">
              <li>Gunakan <strong>Periode</strong> untuk laporan satu bulan tertentu.</li>
              <li>
                Untuk memakai <strong>filter tanggal</strong> (Awal/Akhir Kontrak, Pembayaran, Kepesertaan
                Dibuat), <strong>kosongkan Periode</strong> terlebih dahulu — keduanya tidak dapat dipakai
                bersamaan.
              </li>
              <li>Minimal <strong>satu filter</strong> harus diisi sebelum melakukan export.</li>
            </ul>
          </div>
        </v-alert>
        <v-row>
          <!-- Export Type -->
          <v-col cols="12" md="6">
            <v-select
              v-model="filter.exportType"
              :items="resource.exportType"
              item-value="value"
              item-text="text"
              label="Format Export"
              prepend-icon="mdi-file-excel-outline"
              dense
            />
          </v-col>

          <!-- Periode -->
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="filter.periode"
              :items="resource.periodeList"
              item-value="value"
              item-text="name"
              label="Periode"
              placeholder="Semua Periode"
              prepend-icon="mdi-calendar-month"
              clearable
              dense
              no-data-text="Tidak ditemukan"
            />
          </v-col>

          <template v-if="filter.exportType === 'muawanah'">
            <!-- BMT -->
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="filter.selectedCompany"
                :items="resource.company"
                item-value="value"
                item-text="text"
                label="BMT"
                placeholder="Semua BMT"
                prepend-icon="mdi-office-building-outline"
                clearable
                dense
                :loading="loading.company"
                no-data-text="Tidak ditemukan"
                @change="onCompanyChange"
              />
            </v-col>
            <v-col cols="12" md="6" />

            <!-- Awal Kontrak Dari | Awal Kontrak Sampai -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.startContractFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.startContractFrom"
                    label="Awal Kontrak Dari"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.startContractFrom = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.startContractFrom"
                  no-title
                  @input="menu.startContractFrom = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.startContractTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.startContractTo"
                    label="Awal Kontrak Sampai"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.startContractTo = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.startContractTo"
                  no-title
                  :min="filter.startContractFrom"
                  @input="menu.startContractTo = false"
                />
              </v-menu>
            </v-col>

            <!-- Akhir Kontrak Dari | Akhir Kontrak Sampai -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.endContractFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.endContractFrom"
                    label="Akhir Kontrak Dari"
                    prepend-icon="mdi-calendar-end"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.endContractFrom = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.endContractFrom"
                  no-title
                  @input="menu.endContractFrom = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.endContractTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.endContractTo"
                    label="Akhir Kontrak Sampai"
                    prepend-icon="mdi-calendar-end"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.endContractTo = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.endContractTo"
                  no-title
                  :min="filter.endContractFrom"
                  @input="menu.endContractTo = false"
                />
              </v-menu>
            </v-col>

            <!-- Kepesertaan Dibuat Dari | Kepesertaan Dibuat Sampai -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.createdAtFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.createdAtFrom"
                    label="Kepesertaan Dibuat Dari"
                    prepend-icon="mdi-account-convert"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.createdAtFrom = ''"
                  />
                </template>
                <v-date-picker v-model="filter.createdAtFrom" no-title @input="menu.createdAtFrom = false" />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.createdAtTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.createdAtTo"
                    label="Kepesertaan Dibuat Sampai"
                    prepend-icon="mdi-account-convert"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.createdAtTo = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.createdAtTo"
                  no-title
                  :min="filter.createdAtFrom"
                  @input="menu.createdAtTo = false"
                />
              </v-menu>
            </v-col>
          </template>

          <template v-if="filter.exportType === 'general'">
            <!-- BMT | Produk -->
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="filter.selectedCompany"
                :items="resource.company"
                item-value="value"
                item-text="text"
                label="BMT"
                placeholder="Semua BMT"
                prepend-icon="mdi-office-building-outline"
                clearable
                dense
                :loading="loading.company"
                no-data-text="Tidak ditemukan"
                @change="onCompanyChange"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="filter.selectedProduct"
                :items="resource.product"
                item-value="value"
                item-text="text"
                label="Produk"
                placeholder="Semua Produk"
                prepend-icon="mdi-file-document-multiple-outline"
                clearable
                dense
                :loading="loading.product"
                no-data-text="Tidak ditemukan"
              />
            </v-col>

            <!-- Status Pembayaran -->
            <v-col cols="12" md="6">
              <v-select
                v-model="filter.paymentStatus"
                :items="resource.paymentStatus"
                item-value="value"
                item-text="text"
                label="Status Pembayaran"
                prepend-icon="mdi-cash-check"
                clearable
                dense
              />
            </v-col>
            <v-col cols="12" md="6" />

            <!-- Awal Kontrak Dari | Awal Kontrak Sampai -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.startContractFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.startContractFrom"
                    label="Awal Kontrak Dari"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.startContractFrom = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.startContractFrom"
                  no-title
                  @input="menu.startContractFrom = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.startContractTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.startContractTo"
                    label="Awal Kontrak Sampai"
                    prepend-icon="mdi-calendar"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.startContractTo = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.startContractTo"
                  no-title
                  :min="filter.startContractFrom"
                  @input="menu.startContractTo = false"
                />
              </v-menu>
            </v-col>

            <!-- Akhir Kontrak Dari | Akhir Kontrak Sampai -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.endContractFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.endContractFrom"
                    label="Akhir Kontrak Dari"
                    prepend-icon="mdi-calendar-end"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.endContractFrom = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.endContractFrom"
                  no-title
                  @input="menu.endContractFrom = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.endContractTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.endContractTo"
                    label="Akhir Kontrak Sampai"
                    prepend-icon="mdi-calendar-end"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.endContractTo = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.endContractTo"
                  no-title
                  :min="filter.endContractFrom"
                  @input="menu.endContractTo = false"
                />
              </v-menu>
            </v-col>

            <!-- Tanggal Pembayaran Dari | Tanggal Pembayaran Sampai -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.paymentDateFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.paymentDateFrom"
                    label="Tanggal Pembayaran Dari"
                    prepend-icon="mdi-cash-clock"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.paymentDateFrom = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.paymentDateFrom"
                  no-title
                  @input="menu.paymentDateFrom = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.paymentDateTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.paymentDateTo"
                    label="Tanggal Pembayaran Sampai"
                    prepend-icon="mdi-cash-clock"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.paymentDateTo = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.paymentDateTo"
                  no-title
                  :min="filter.paymentDateFrom"
                  @input="menu.paymentDateTo = false"
                />
              </v-menu>
            </v-col>

            <!-- Kepesertaan Dibuat Dari | Kepesertaan Dibuat Sampai -->
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.createdAtFrom"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.createdAtFrom"
                    label="Kepesertaan Dibuat Dari"
                    prepend-icon="mdi-account-convert"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.createdAtFrom = ''"
                  />
                </template>
                <v-date-picker v-model="filter.createdAtFrom" no-title @input="menu.createdAtFrom = false" />
              </v-menu>
            </v-col>
            <v-col cols="12" md="6">
              <v-menu
                v-model="menu.createdAtTo"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filter.createdAtTo"
                    label="Kepesertaan Dibuat Sampai"
                    prepend-icon="mdi-account-convert"
                    readonly
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filter.createdAtTo = ''"
                  />
                </template>
                <v-date-picker
                  v-model="filter.createdAtTo"
                  no-title
                  :min="filter.createdAtFrom"
                  @input="menu.createdAtTo = false"
                />
              </v-menu>
            </v-col>
          </template>
        </v-row>
      </v-container>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4">
      <v-btn text @click="resetFilter">
        <v-icon left>mdi-refresh</v-icon>
        Reset
      </v-btn>
      <v-spacer />
      <v-btn text @click="onCloseButtonClick">Batal</v-btn>
      <v-btn color="success" :loading="loading.export" @click="exportExcel">
        <v-icon left>mdi-microsoft-excel</v-icon>
        Export Excel
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
/* eslint-disable no-unused-vars */
import Swal from 'sweetalert2'
import Constant from '@/const'
import ConstantMixin from '@/constantMixin'

const _ = require('lodash')
const axios = require('axios')

export default {
  mixins: [ConstantMixin],
  props: {
    defaultCompany: {
      type: String,
      default: ''
    },
    defaultPaymentStatus: {
      type: String,
      default: ''
    },
    defaultPeriode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: {
        company: false,
        product: false,
        export: false
      },
      menu: {
        startContractFrom: false,
        startContractTo: false,
        endContractFrom: false,
        endContractTo: false,
        paymentDateFrom: false,
        paymentDateTo: false,
        createdAtFrom: false,
        createdAtTo: false
      },
      filter: {
        selectedCompany: '',
        selectedProduct: '',
        exportType: 'general',
        periode: '',
        startContractFrom: '',
        startContractTo: '',
        endContractFrom: '',
        endContractTo: '',
        paymentStatus: '',
        paymentDateFrom: '',
        paymentDateTo: '',
        createdAtFrom: '',
        createdAtTo: ''
      },
      resource: {
        company: [],
        product: [],
        periodeList: [],
        exportType: [
          { value: 'general', text: 'Taawun' },
          { value: 'muawanah', text: 'Muawanah' },
          { value: 'brins', text: 'BRINS' }
        ],
        paymentStatus: [
          { value: 'approved', text: 'Sudah Dibayar' },
          { value: 'requested', text: 'Menunggu Konfirmasi' }
        ]
      }
    }
  },
  computed: {
    hasDateFilter() {
      const f = this.filter
      return !!(
        f.startContractFrom ||
        f.startContractTo ||
        f.endContractFrom ||
        f.endContractTo ||
        f.paymentDateFrom ||
        f.paymentDateTo ||
        f.createdAtFrom ||
        f.createdAtTo
      )
    },
    hasAnyFilter() {
      const f = this.filter
      return !!(f.periode || f.selectedCompany || f.selectedProduct || f.paymentStatus) || this.hasDateFilter
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.resource.periodeList = this.monthFilter()
      this.filter.selectedCompany = this.defaultCompany || ''
      this.filter.paymentStatus = this.defaultPaymentStatus || ''
      this.filter.periode = this.defaultPeriode || ''
      this.getAllCompany()
      this.getAllProduct()
    })
  },
  methods: {
    getAllCompany() {
      this.loading.company = true
      this.runApiCalls(() => {
        axios
          .get(Constant.apiUrl.concat('/master/company?headquarters=1'))
          .then(response => {
            if (response.status === 200) {
              this.resource.company = response.data.data
                .filter(v => v.name !== 'ADMINISTRATOR' && v.isActive)
                .map(company => ({
                  value: _.get(company, '_id', '').toString(),
                  text: _.get(company, 'name', '')
                }))
            }
          })
          .catch(error => this.showErrorAxios(error))
          .finally(() => {
            this.loading.company = false
          })
      })
    },
    getAllProduct() {
      this.loading.product = true
      this.runApiCalls(() => {
        axios
          .get(Constant.apiUrl.concat('/product/active?sort=name'))
          .then(response => {
            if (response.status === 200) {
              this.resource.product = response.data.data.map(product => ({
                value: _.get(product, '_id', '').toString(),
                text: _.get(product, 'name', '')
              }))
            }
          })
          .catch(error => this.showErrorAxios(error))
          .finally(() => {
            this.loading.product = false
          })
      })
    },
    onCompanyChange(val) {
      if (!val) this.filter.selectedCompany = ''
    },
    resetFilter() {
      this.filter = {
        selectedCompany: this.defaultCompany || '',
        selectedProduct: '',
        exportType: 'general',
        periode: this.defaultPeriode || '',
        startContractFrom: '',
        startContractTo: '',
        endContractFrom: '',
        endContractTo: '',
        paymentStatus: this.defaultPaymentStatus || '',
        paymentDateFrom: '',
        paymentDateTo: '',
        createdAtFrom: '',
        createdAtTo: ''
      }
    },
    buildQueryParams() {
      const companyName = this.$store.getters.userLoggedIn.company.name
      const params = {
        company: companyName,
        exporttype: this.filter.exportType || 'general'
      }
      if (this.filter.selectedCompany) {
        params.filtercompany = this.filter.selectedCompany
        params.isheadquarter = true
      }
      if (this.filter.selectedProduct) params.product = this.filter.selectedProduct
      if (this.filter.periode && !this.filter.startContractFrom && !this.filter.startContractTo)
        params.periode = this.filter.periode
      if (this.filter.startContractFrom) params.disbursementStart = this.filter.startContractFrom
      if (this.filter.startContractTo) params.disbursementEnd = this.filter.startContractTo
      if (this.filter.endContractFrom) params.dueDateStart = this.filter.endContractFrom
      if (this.filter.endContractTo) params.dueDateEnd = this.filter.endContractTo
      if (this.filter.paymentStatus) params.filterPaidStatus = this.filter.paymentStatus
      if (this.filter.paymentDateFrom) params.paidDateStart = this.filter.paymentDateFrom
      if (this.filter.paymentDateTo) params.paidDateEnd = this.filter.paymentDateTo
      if (this.filter.createdAtFrom) params.createdStart = this.filter.createdAtFrom
      if (this.filter.createdAtTo) params.createdEnd = this.filter.createdAtTo
      return params
    },
    exportExcel() {
      if (!this.hasAnyFilter) {
        Swal.fire({
          icon: 'warning',
          title: 'Filter Kosong',
          text: 'Minimal salah satu filter harus diisi sebelum melakukan export.'
        })
        return
      }
      this.loading.export = true
      this.runApiCalls(() => {
        try {
          const params = this.buildQueryParams()
          const query = new URLSearchParams(params).toString()
          const fileNameMap = {
            general: 'Laporan Kepesertaan Taawun',
            muawanah: 'Laporan Kepesertaan Muawanah',
            brins: 'Laporan Kepesertaan BRINS'
          }
          const fileName = fileNameMap[this.filter.exportType] || 'Laporan Kepesertaan'
          const fileLink = document.createElement('a')
          fileLink.href = Constant.apiUrl.concat(`/reporting/xlsx?${query}`)
          fileLink.setAttribute('download', `${fileName}.xlsx`)
          document.body.appendChild(fileLink)
          fileLink.click()
          document.body.removeChild(fileLink)
          this.loading.export = false
        } catch (error) {
          this.loading.export = false
          Swal.fire({
            icon: 'error',
            title: 'Terjadi Kesalahan',
            text: 'Gagal mengunduh file Excel'
          })
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped></style>
